<template>
    <div class="personalize-stepper-wrapper__form ion-padding">
        <ion-row>
            <ion-col size-md="12" size-lg="12">
                <section class="questionnaire-wrapper">
                    <!-- Header -->
                    <div class="questionnaire-header">
                        <h2 class="title" style="color: '#16163F';">{{ t('questionnaire.title') }}</h2>
                        <img :src="questionnaireLogo" alt="Welcome" class="illustration" />
                        <p class="description" v-if="question">{{ question.questionLabel }}</p>
                    </div>

                    <!-- Answer list -->
                    <ion-list v-if="question" class="questionnaire-list">
                        <ion-item class="questionnaire-item" v-for="answer in question.answers" :key="answer.answerKey"
                            button @click="toggleAnswer(answer)">
                            <ion-icon :icon="getIcon(answer.icon)" slot="start" class="questionnaire-icon" />
                            <ion-label class="questionnaire-label">{{ answer.answerLabel }}</ion-label>
                            <ion-checkbox slot="end" :checked="!!selectedAnswers[answer.answerKey]" disabled />
                        </ion-item>
                    </ion-list>

                    <!-- Footer -->
                    <div class="questionnaire-footer">
                        <ion-button expand="block" shape="round" class="submit-btn" @click="submit">
                            {{ t('questionnaire.submit') }}
                        </ion-button>
                        <ion-button expand="block" fill="clear" class="skip-btn" @click="goTo('Explore')">
                            {{ t('questionnaire.skip') }}
                        </ion-button>
                    </div>

                    <!-- Detail modal -->
                    <ion-modal :is-open="detailModalOpen" @didDismiss="closeModal">
                        <ion-content class="ion-padding">
                            <div class="detail-header">
                                <h3 class="ion-text-center">{{ t('questionnaire.modal.title') }}</h3>
                                <img :src="questionnaireLogo" alt="Detail" class="detail-illustration" />
                            </div>

                            <ion-item>
                                <ion-label position="floating" style="margin-bottom: 12px;">{{
                                    t('questionnaire.modal.placeholder') }}</ion-label>
                                <ion-input v-model="detailInput" />
                            </ion-item>

                            <ion-grid class="ion-padding-top">
                                <ion-row>
                                    <ion-col size="6">
                                        <ion-button expand="block" fill="outline" @click="closeModal">
                                            {{ t('questionnaire.modal.back') }}
                                        </ion-button>
                                    </ion-col>
                                    <ion-col size="6">
                                        <ion-button expand="block" @click="saveDetail">
                                            {{ t('questionnaire.modal.submit') }}
                                        </ion-button>
                                    </ion-col>
                                </ion-row>
                            </ion-grid>
                        </ion-content>
                    </ion-modal>
                </section>
            </ion-col>
        </ion-row>
    </div>
    <ion-loading :is-open="loading" message="Loading..." spinner="crescent" />

</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { IonModal, IonContent, IonButton, IonItem, IonLabel, IonCheckbox, IonInput, IonIcon, IonGrid, IonRow, IonCol, IonLoading } from '@ionic/vue'
import { school, home, business, people, globe, mail, helpCircle } from 'ionicons/icons'
import { getData, postData } from '@/services/axios-service'
import { BASE_URL } from '@/utils/constants/api-constants'
import questionnaireLogo from '@/assets/images/questionnaire.svg';
import { useI18n } from 'vue-i18n';
import { useAuth } from '@/composables/useAuth'
import { useToast } from '@/composables/useToast';

type Answer = {
    answerKey: string
    answerLabel: string
    hasDetail: boolean
    icon: string
    detail?: string
}

type Question = {
    questionKey: string
    questionLabel: string
    required: boolean
    answers: Answer[]
}

const { t } = useI18n();
const loading = ref(false)
const question = ref<Question | null>(null)
const selectedAnswers = ref<Record<string, Answer>>({})
const detailModalOpen = ref(false)
const activeDetailKey = ref<string | null>(null)
const detailInput = ref('')
const { goTo } = useAuth();
const { showToast } = useToast();



const iconMap: Record<string, string> = {
    university: school,
    owner: home,
    company: business,
    friend: people,
    social: globe,
    email: mail,
    other: helpCircle
}

onMounted(async () => {
    loading.value = true;

    const response = await getData<Question[]>(`${BASE_URL.USERS}profile/questionnaire`);

    if (response.success && Array.isArray(response.data) && response.data.length > 0) {
        question.value = response.data[0];
    }

    loading.value = false;
});



const getIcon = (key: string): string => iconMap[key] || helpCircle

const toggleAnswer = (answer: Answer) => {
    const key = answer.answerKey

    if (selectedAnswers.value[key]) {
        delete selectedAnswers.value[key]
    } else {
        selectedAnswers.value[key] = { ...answer }

        if (answer.hasDetail) {
            activeDetailKey.value = key
            detailInput.value = selectedAnswers.value[key].detail || ''
            detailModalOpen.value = true
        }
    }
}

const saveDetail = () => {
    if (activeDetailKey.value && selectedAnswers.value[activeDetailKey.value]) {
        selectedAnswers.value[activeDetailKey.value].detail = detailInput.value.trim()
    }
    closeModal()
}

const closeModal = () => {
    detailModalOpen.value = false
    detailInput.value = ''
    activeDetailKey.value = null
}

const submit = async () => {
    const payload = Object.values(selectedAnswers.value).map(({ answerKey, detail }) => ({
        answerKey,
        ...(detail && { detail }),
    }));

    const body = {
        questionKey: question.value?.questionKey,
        answers: payload,
    };

    console.log('[Questionnaire] Submit payload:', body);

    const response = await postData(`${BASE_URL.USERS}profile/questionnaire`, body);

    if (response.success && response.msg) {
        await showToast(response.msg, "success");
        goTo('Explore');

    } else {
        console.error('Error saving questionnaire:', response.msg);
    }
};


</script>


<style scoped>
ion-modal {
    --height: 50%;
    --border-radius: 16px;
    --box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
}


.custom-item {
    --background: #fff;
    --ion-item-background: #fff;
    --inner-border-width: 0;
    border: 1px solid #ccc;
    border-radius: 9999px;
    margin-bottom: 0.5rem;
    padding: 0.75rem 1rem;
    align-items: center;
}

.title {
    color: #16163F;
}

.questionnaire-wrapper {
    background: white;
    border-radius: 20px;
    padding: 24px;
    text-align: center;
}

.questionnaire-header .title {
    font-size: 20px;
    font-weight: 600;
}

.illustration,
.detail-illustration {
    margin: 16px auto;
    width: 120px;
    display: block;
}

.description {
    color: #6e6e6e;
    font-size: 14px;
    white-space: pre-line;
}

.questionnaire-list {
    background-color: #fff;
    margin-top: 24px;
}

.questionnaire-item {
    --background: #fff;
    --ion-item-background: transparent;
    --inner-border-width: 0;
    border: 1px solid #dcdcdc;
    border-radius: 9999px;
    margin-bottom: 24px;
    box-shadow: none;
}


.questionnaire-label {
    font-size: 15px;
    font-weight: 500;
}

.questionnaire-icon {
    color: #1c4197;
}

.questionnaire-footer {
    margin-top: 32px;
}

.submit-btn {
    color: white;
}

.skip-btn {
    margin-top: 8px;
    color: #1c4197;
    font-weight: 500;
}
</style>